import {
    Flex,
    List,
    Button,
    useStyles,
    Caption,
    useTheme,
    pxToRem,
} from '@archipro-design/aria';
import { HeartFill, HeartLine, StarFill } from '@archipro-design/icons';
import { Link, useLoaderData } from 'react-router';
import { localizeTime } from '@modules/root';
import * as S from './Stats.style';
import { FollowSelector } from '~/modules/moodboards/components';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import VerifiedMemberBadge from '~/modules/detail/components/verified-member-badge/VerifiedMemberBadge';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { ProductProfessional } from '../../type/product-professional';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

type CompanyInfoData = Pick<
    ProductProfessional,
    'URLSegment' | 'ProfileLiveDate' | 'ID' | 'Website'
>;
interface CompanyInfoStatsProps {
    data: CompanyInfoData;
}

const getMetricData = (data: CompanyInfoData) => {
    const profilePage = `/professional/${data.URLSegment}`;
    const webUrl = data.Website;
    const memberSinceYear = localizeTime(data.ProfileLiveDate).format('YYYY');

    return {
        webUrl,
        profilePage,
        memberSinceYear,
    };
};

const CompanyInfoStats = ({ data }: CompanyInfoStatsProps) => {
    const { css } = useStyles();
    const { siteVariables } = useTheme();

    const REVIEWS_READY = false;

    const { webUrl, profilePage, memberSinceYear } = getMetricData(data);

    const { isInDesignBoard } = useHandleFollow();

    const { product } = useLoaderData<typeof loader>();

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(webUrl, {
            libraryItemID: product.ID,
            professionalID: product.Professional.ID,
            professionalTitle: product.Professional.Title,
            source: 'ProductAboutSeller',
        });

    const companyInfoStats = [
        // ECOM-1087/1088: OUT OF SCOPE.
        ...(REVIEWS_READY
            ? [
                  {
                      label: 'Reviews',
                      customChildComponent: (
                          <Flex>
                              <Flex className={css(S.ReviewsStarContainer)}>
                                  {[...Array(5)].map((_, i) => (
                                      <StarFill
                                          key={`review-star-${i}`}
                                          className={css(S.StarIcon)}
                                      />
                                  ))}
                              </Flex>

                              <Caption className={css(S.ReviewsCount)}>
                                  (6 reviews)
                              </Caption>
                          </Flex>
                      ),
                  },
              ]
            : []),

        // ...(suburbCity
        //     ? [
        //           {
        //               label: 'Location',
        //               customChildComponent: (
        //                   <Link
        //                       data-testid="location"
        //                       style={{
        //                           textDecoration: 'underline',
        //                           cursor: 'pointer',
        //                           color: 'inherit',
        //                       }}
        //                       to={`${product.Professional.Link}/contact#${PROFESSIONAL_SECTIONS_ANCHOR}`}
        //                       onClick={() => onRevealClick()}
        //                   >
        //                       {locationsCount > 1
        //                           ? 'View all locations'
        //                           : 'View address'}{' '}
        //                       {locationsCount > 1 &&
        //                           `(+${String(locationsCount - 1)})`}
        //                   </Link>
        //               ),
        //           },
        //       ]
        //     : []),
        {
            label: 'More information',
            customChildComponent: (
                <Flex space="between" className={css(S.WebsiteStat)}>
                    {webUrl ? (
                        <Button
                            as="a"
                            href={referralTrackingURL}
                            target="_blank"
                            size={16}
                            text
                            color="dark"
                            variables={{
                                darkButtonTextColorHover:
                                    siteVariables.colors.grey['200'],
                                buttonPadding: 0,
                            }}
                            className={css({
                                textDecoration: 'underline!important',
                            })}
                            data-testid="website-link"
                            {...referralTrackingParams}
                        >
                            Visit Product website
                        </Button>
                    ) : (
                        <div />
                    )}
                    <Flex
                        vAlign="center"
                        gap="space-24"
                        className={css(S.FavouriteGroup)}
                    >
                        <Button
                            color="dark"
                            className={css(S.ProfileButton)}
                            as={Link}
                            to={profilePage}
                            size={16}
                            variables={{
                                buttonSize24LetterSpacing:
                                    siteVariables.letterSpacingMedium,
                            }}
                        >
                            View Profile
                        </Button>
                        <FollowSelector
                            trigger={
                                <Button
                                    className={css(S.HeartButton)}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                    icon={
                                        isInDesignBoard(
                                            data.ID,
                                            'ProfessionalPin',
                                        ) ? (
                                            <HeartFill size="2x" />
                                        ) : (
                                            <HeartLine size="2x" />
                                        )
                                    }
                                    variables={{
                                        buttonSize36IconXMargin: '0',
                                        buttonPadding: pxToRem(8),
                                    }}
                                />
                            }
                            pinId={data.ID}
                            pinType="ProfessionalPin"
                        />
                    </Flex>
                </Flex>
            ),
        },
    ];

    return (
        <div className={css(S.CompanyInfoContainer)}>
            <div className="mb-5">
                <VerifiedMemberBadge
                    profileLiveDate={data.ProfileLiveDate ?? memberSinceYear}
                />
            </div>
            <List
                items={companyInfoStats}
                className={css(S.CompanyInfoList)}
                variables={{
                    listBorderColor: siteVariables.colors.primitive.black,
                }}
            />
        </div>
    );
};

export default CompanyInfoStats;
